import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Shield, Dumbbell, ChevronLeft, Plus, Trash2, Edit, X, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CATEGORIES = ["bryst", "ryg", "skuldre", "arme", "ben", "mave", "helkrop", "cardio", "mobilitet"];
const DIFFICULTIES = [
  { value: "beginner", label: "Begynder" },
  { value: "intermediate", label: "Middel" },
  { value: "advanced", label: "Avanceret" },
];

export default function AdminExercises() {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: "", category: "bryst", description: "", difficulty: "beginner", video_url: "", instructions: "", muscles_worked: ""
  });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem("admin_token");
    if (!token) { navigate("/admin"); return null; }
    return { Authorization: `Bearer ${token}` };
  };

  const fetchExercises = () => {
    const headers = getAuthHeaders();
    if (!headers) return;
    setLoading(true);
    axios.get(`${API}/admin/exercises`, { headers })
      .then(res => setExercises(res.data))
      .catch(err => {
        if (err.response?.status === 401 || err.response?.status === 403) {
          localStorage.removeItem("admin_token");
          navigate("/admin");
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchExercises(); }, []);

  const resetForm = () => {
    setForm({ name: "", category: "bryst", description: "", difficulty: "beginner", video_url: "", instructions: "", muscles_worked: "" });
    setEditingId(null);
    setShowForm(false);
  };

  const openEdit = (exercise) => {
    setForm({
      name: exercise.name,
      category: exercise.category,
      description: exercise.description || "",
      difficulty: exercise.difficulty || "beginner",
      video_url: exercise.video_url || "",
      instructions: (exercise.instructions || []).join("\n"),
      muscles_worked: (exercise.muscles_worked || []).join(", ")
    });
    setEditingId(exercise.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.description.trim()) {
      toast.error("Udfyld navn og beskrivelse");
      return;
    }

    const headers = getAuthHeaders();
    if (!headers) return;

    setSaving(true);
    const payload = {
      name: form.name,
      category: form.category,
      description: form.description,
      difficulty: form.difficulty,
      video_url: form.video_url || null,
      instructions: form.instructions.split("\n").filter(i => i.trim()),
      muscles_worked: form.muscles_worked.split(",").map(m => m.trim()).filter(m => m)
    };

    try {
      if (editingId) {
        await axios.put(`${API}/admin/exercises/${editingId}`, payload, { headers });
        toast.success("Øvelse opdateret");
      } else {
        await axios.post(`${API}/admin/exercises`, payload, { headers });
        toast.success("Øvelse oprettet");
      }
      resetForm();
      fetchExercises();
    } catch {
      toast.error("Fejl ved gemning");
    } finally {
      setSaving(false);
    }
  };

  const deleteExercise = async (id) => {
    if (!window.confirm("Er du sikker på du vil slette denne øvelse?")) return;
    const headers = getAuthHeaders();
    if (!headers) return;
    try {
      await axios.delete(`${API}/admin/exercises/${id}`, { headers });
      toast.success("Øvelse slettet");
      fetchExercises();
    } catch {
      toast.error("Fejl ved sletning");
    }
  };

  const groupedExercises = exercises.reduce((acc, ex) => {
    if (!acc[ex.category]) acc[ex.category] = [];
    acc[ex.category].push(ex);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#09090b] via-[#0c0c0f] to-[#09090b]">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-black/70 border-b border-red-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Link to="/admin/dashboard" className="flex items-center gap-2 text-zinc-400 hover:text-white">
                <ChevronLeft className="h-5 w-5" />
              </Link>
              <div className="w-9 h-9 rounded-lg bg-red-500 flex items-center justify-center">
                <Shield className="h-5 w-5 text-white" />
              </div>
              <span style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-lg uppercase tracking-wider text-white">
                Øvelser
              </span>
            </div>
            <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-red-500 hover:bg-red-600 text-white rounded-lg">
              <Plus className="h-4 w-4 mr-2" /> Ny Øvelse
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-exercises">
        <div className="mb-6">
          <h1 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-2xl uppercase tracking-tight text-white mb-1">
            Øvelses Administration
          </h1>
          <p className="text-sm text-zinc-400">{exercises.length} øvelser i alt</p>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1,2,3].map(i => <div key={i} className="bg-[#18181b] rounded-xl h-32 animate-pulse" />)}
          </div>
        ) : (
          <div className="space-y-8">
            {CATEGORIES.map(cat => {
              const catExercises = groupedExercises[cat] || [];
              if (catExercises.length === 0) return null;
              return (
                <div key={cat}>
                  <h2 style={{ fontFamily: 'Oswald, sans-serif' }} className="uppercase text-sm tracking-wider text-[#DFFF00] mb-3 flex items-center gap-2">
                    <Dumbbell className="h-4 w-4" /> {cat} ({catExercises.length})
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {catExercises.map(ex => (
                      <div key={ex.id} className="bg-[#18181b] border border-white/5 rounded-xl p-4 hover:border-white/10 transition-all group">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-white truncate">{ex.name}</p>
                            <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{ex.description}</p>
                          </div>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button variant="ghost" size="icon" onClick={() => openEdit(ex)} className="h-8 w-8 text-zinc-400 hover:text-white">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => deleteExercise(ex.id)} className="h-8 w-8 text-zinc-400 hover:text-red-400">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-[#18181b] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Oswald, sans-serif' }} className="uppercase text-white">
              {editingId ? "Rediger Øvelse" : "Ny Øvelse"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Navn</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                className="bg-zinc-900/50 border-zinc-800 rounded-lg h-10 mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Kategori</Label>
                <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                  <SelectTrigger className="bg-zinc-900/50 border-zinc-800 rounded-lg h-10 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Sværhedsgrad</Label>
                <Select value={form.difficulty} onValueChange={v => setForm({...form, difficulty: v})}>
                  <SelectTrigger className="bg-zinc-900/50 border-zinc-800 rounded-lg h-10 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {DIFFICULTIES.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Beskrivelse</Label>
              <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                className="bg-zinc-900/50 border-zinc-800 rounded-lg h-10 mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">YouTube Video URL</Label>
              <Input value={form.video_url} onChange={e => setForm({...form, video_url: e.target.value})}
                className="bg-zinc-900/50 border-zinc-800 rounded-lg h-10 mt-1" placeholder="https://youtube.com/watch?v=..." />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Instruktioner (én per linje)</Label>
              <textarea value={form.instructions} onChange={e => setForm({...form, instructions: e.target.value})}
                className="w-full bg-zinc-900/50 border border-zinc-800 rounded-lg p-3 text-sm text-white mt-1 min-h-[80px]" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Muskler (kommasepareret)</Label>
              <Input value={form.muscles_worked} onChange={e => setForm({...form, muscles_worked: e.target.value})}
                className="bg-zinc-900/50 border-zinc-800 rounded-lg h-10 mt-1" placeholder="bryst, triceps, skuldre" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={resetForm} className="flex-1 border-zinc-700">
                Annuller
              </Button>
              <Button type="submit" disabled={saving} className="flex-1 bg-red-500 hover:bg-red-600">
                <Save className="h-4 w-4 mr-2" /> {saving ? "Gemmer..." : "Gem"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
